import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { Sparkles, Pencil, Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, usePagination } from '@/components/Pagination';

const CATEGORIES = [
  { v: 'producto_nuevo', label: 'Producto nuevo' },
  { v: 'categoria_nueva', label: 'Nueva categoría' },
  { v: 'promocion', label: 'Promoción' },
  { v: 'plataforma', label: 'Plataforma' },
];

const EMPTY = { title: '', body: '', category: 'plataforma', image_url: '', is_published: false };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | full obj for edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { pageItems, page, setPage, totalPages, total: pagTotal } = usePagination(items);

  useSEO({
    title: 'Novedades | Admin | AutoMarket',
    description: 'Gestión de novedades y anuncios de la plataforma.',
    canonical: '/admin/novedades',
  });

  const load = () => {
    setLoading(true);
    api.get('/admin/announcements', { params: { limit: 100 } })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch(() => toast.error('No se pudo cargar el listado'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (a) => {
    setForm({
      title: a.title || '',
      body: a.body || '',
      category: a.category || 'plataforma',
      image_url: a.image_url || '',
      is_published: !!a.is_published,
    });
    setEditing(a);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.trim().length < 3) return toast.error('Título mínimo 3 caracteres');
    if (!form.body.trim() || form.body.trim().length < 3) return toast.error('Contenido mínimo 3 caracteres');
    setSaving(true);
    try {
      if (editing && editing.announcement_id) {
        await api.patch(`/admin/announcements/${editing.announcement_id}`, form);
        toast.success('Novedad actualizada');
      } else {
        await api.post('/admin/announcements', form);
        toast.success('Novedad creada');
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.announcement_id}`, { is_published: !a.is_published });
      toast.success(a.is_published ? 'Despublicada' : 'Publicada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.announcement_id}`);
      toast.success('Eliminada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Admin</div>
            <h1 className="font-display text-3xl font-black tracking-tighter">Novedades</h1>
          </div>
          <div className="flex-1" />
          <Button
            data-testid="admin-ann-new-btn"
            onClick={openNew}
            className="rounded-none bg-orange-500 text-slate-900 hover:bg-orange-400"
          >
            <Plus className="h-4 w-4 mr-1" /> Nueva novedad
          </Button>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {loading ? (
          <p className="text-center text-slate-500 py-12">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-500 py-12" data-testid="admin-ann-empty">Sin novedades. Crea la primera.</p>
        ) : (
          <div className="border border-slate-200 bg-white" data-testid="admin-ann-list">
            <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <div className="col-span-4">Título</div>
              <div className="col-span-2">Categoría</div>
              <div className="col-span-2">Estado</div>
              <div className="col-span-2">Actualizada</div>
              <div className="col-span-2 text-right">Acciones</div>
            </div>
            {pageItems.map((a) => (
              <div key={a.announcement_id} className="grid grid-cols-12 gap-3 border-b border-slate-100 px-4 py-3 items-center text-sm">
                <div className="col-span-4 font-semibold text-slate-900">{a.title}</div>
                <div className="col-span-2 text-slate-600 text-xs">{CATEGORIES.find(c => c.v === a.category)?.label || a.category}</div>
                <div className="col-span-2">
                  <span data-testid={`ann-state-${a.is_published ? 'published' : 'draft'}`} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.is_published ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-700'}`}>
                    {a.is_published ? 'Publicada' : 'Borrador'}
                  </span>
                </div>
                <div className="col-span-2 text-slate-500 text-xs">{(a.updated_at || a.created_at || '').substring(0, 10)}</div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button data-testid={`ann-toggle-${a.announcement_id}`} onClick={() => togglePublish(a)} title={a.is_published ? 'Despublicar' : 'Publicar'} className="p-2 hover:bg-slate-100">
                    {a.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button data-testid={`ann-edit-${a.announcement_id}`} onClick={() => openEdit(a)} className="p-2 hover:bg-slate-100">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`ann-delete-${a.announcement_id}`} onClick={() => remove(a)} className="p-2 hover:bg-red-50 text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">Total: {total}</div>
          </div>
        )}
        <Pagination page={page} totalPages={totalPages} total={pagTotal} setPage={setPage} testIdPrefix="admin-ann-pag" />
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" onClick={close}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="w-full max-w-2xl bg-white p-6 space-y-4"
            data-testid="admin-ann-form"
          >
            <h2 className="font-display text-2xl font-black">{editing.announcement_id ? 'Editar novedad' : 'Nueva novedad'}</h2>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Título *</label>
              <Input
                data-testid="admin-ann-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="rounded-none mt-1"
                maxLength={140}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Categoría *</label>
              <select
                data-testid="admin-ann-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm"
              >
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Imagen destacada (URL opcional)</label>
              <Input
                data-testid="admin-ann-image"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="rounded-none mt-1"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Contenido *</label>
              <Textarea
                data-testid="admin-ann-body"
                required
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="rounded-none mt-1"
                maxLength={8000}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="ann-published"
                data-testid="admin-ann-published"
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              />
              <label htmlFor="ann-published" className="text-sm text-slate-700">Publicar inmediatamente</label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={close} className="rounded-none">Cancelar</Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="admin-ann-save-btn"
                className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
